import {clone, isEqual} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

type Filters = any;

type WrapperChildrenProps<T = any> = {
    filters: T;
    filtersDirty: boolean;
    filtersEqualDefault: boolean;
    clearFilters: () => void;
    applyFilters: () => void;
    onFiltersChange: (filters: Partial<T>, options?: {apply: boolean}) => void;
};

export type FiltersWrapperChildrenProps<T> = WrapperChildrenProps<T>;

type WrapperProps = {
    defaultFilters: Filters,
    filters: Filters | null,
    compareFilters?: (filters1: Filters, filters2: Filters) => boolean,
    children: (props: WrapperChildrenProps) => React.ReactElement;
    onSubmit: (filters: Filters) => void;
};

export const FiltersWrapper: React.FC<WrapperProps> = ({
    filters, defaultFilters, children, compareFilters: compareFiltersProp, onSubmit
}: WrapperProps) => {
    const defaultFiltersClone = useMemo(() => clone(defaultFilters), [defaultFilters]);
    const [localFilters, setLocalFilters] = useState<Filters>(clone(defaultFiltersClone));

    useEffect(() => {
        if (filters) {
            setLocalFilters(filters);
        }
    }, [filters]);

    const getMergedFilters = useCallback((changes: Partial<Filters>) => ({...localFilters, ...changes}), [localFilters]);

    const handleSubmit = useCallback((_filters: Filters = localFilters) => {
        onSubmit({..._filters});
    }, [onSubmit, localFilters]);

    const applyFilters = useCallback(() => {
        handleSubmit();
    }, [handleSubmit]);

    const onFiltersChange = useCallback((changes: Partial<Filters>, options?: {apply: boolean}) => {
        const newFilters = getMergedFilters(changes);
        setLocalFilters(newFilters);
        if (options?.apply) {
            handleSubmit(newFilters);
        }
    }, [getMergedFilters, handleSubmit]);

    const compareFilters = useCallback((filters1: Filters, filters2: Filters) =>
        compareFiltersProp ? compareFiltersProp(filters1, filters2) : isEqual(filters1, filters2),
    [compareFiltersProp]);

    const filtersEqualDefault = useMemo(() => compareFilters(defaultFiltersClone, localFilters),
        [compareFilters, defaultFiltersClone, localFilters]);

    const filtersDirty = useMemo(() => !!filters && !compareFilters(filters, localFilters), [compareFilters, filters, localFilters]);

    const clearFilters = useCallback(() => {
        onFiltersChange(defaultFiltersClone);
        onSubmit(defaultFiltersClone);
    }, [onFiltersChange, defaultFiltersClone, onSubmit]);

    const childrenProps = useMemo(() => ({
        filters: localFilters,
        filtersDirty,
        filtersEqualDefault,
        compareFiltersProp,
        onFiltersChange,
        clearFilters,
        applyFilters
    }), [localFilters, filtersDirty, filtersEqualDefault, compareFiltersProp, onFiltersChange, clearFilters, applyFilters]);

    return children(childrenProps);
};
